import React, { useEffect, useState, useRef } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FIELDS = [
  { key: "fecha", label: "Fecha *", required: true },
  { key: "descripcion", label: "Descripción / Glosa" },
  { key: "cargo", label: "Cargo / Débito" },
  { key: "abono", label: "Abono / Crédito" },
  { key: "saldo", label: "Saldo" },
  { key: "n_doc", label: "N° doc / Operación" },
];

export default function Cartola() {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState(todayISO());

  const [importOpen, setImportOpen] = useState(false);
  const [parsed, setParsed] = useState(null); // {headers, rows, suggested_mapping}
  const [mapping, setMapping] = useState({});
  const [reemplazar, setReemplazar] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const loadCuentas = async () => {
    try {
      const { data } = await api.get("/cuentas-bancarias");
      setCuentas(data);
      if (data.length > 0 && !cuentaId) setCuentaId(data[0].id);
    } catch (e) { toast.error(extractError(e)); }
  };

  const loadCartola = async () => {
    if (!cuentaId) return;
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get(`/cartola/${cuentaId}`, { params });
      setData(data);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCuentas(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCartola(); }, [cuentaId, desde, hasta]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.info("Analizando archivo…");
      const { data } = await api.post("/cartola/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      setMapping(data.suggested_mapping || {});
      setImportOpen(true);
    } catch (err) { toast.error(extractError(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const submitImport = async () => {
    if (!mapping.fecha) { toast.error("Mapea al menos la columna Fecha"); return; }
    if (!mapping.cargo && !mapping.abono) { toast.error("Mapea al menos Cargo o Abono"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/cartola/import", {
        cuenta_bancaria_id: cuentaId,
        mapping,
        rows: parsed.rows,
        reemplazar,
      });
      toast.success(`Importadas ${data.imported} · Omitidas ${data.skipped}` + (data.errors.length ? ` · ${data.errors.length} errores` : ""));
      setImportOpen(false); setParsed(null); setMapping({});
      loadCartola(); loadCuentas();
    } catch (err) { toast.error(extractError(err)); }
    finally { setImporting(false); }
  };

  const delMov = async (mid) => {
    if (!window.confirm("¿Eliminar movimiento?")) return;
    try { await api.delete(`/movimientos-bancarios/${mid}`); toast.success("Eliminado"); loadCartola(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <Page>
      <PageHeader
        title="Cartola bancaria"
        subtitle="Movimientos importados desde el banco"
        testid="cart-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={cuentaId} onValueChange={setCuentaId}>
              <SelectTrigger className="text-sm h-9 w-64"><SelectValue placeholder="Seleccionar cuenta..." /></SelectTrigger>
              <SelectContent>
                {cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{c.banco} · {c.numero}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="px-2 py-1.5 border border-[#E2DFD8] rounded-sm text-sm" />
            <span className="text-xs text-[#59645E]">→</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="px-2 py-1.5 border border-[#E2DFD8] rounded-sm text-sm" />
            <input type="file" ref={fileRef} onChange={handleFile} accept=".xlsx,.xls,.csv" className="hidden" data-testid="cart-file-input" />
            <label className="inline-flex items-center gap-1.5 text-xs text-[#59645E] cursor-pointer select-none px-2 py-1.5 rounded-sm hover:bg-[#F3F1EC]" title="Reemplazar movimientos no conciliados">
              <input type="checkbox" checked={reemplazar} onChange={(e) => setReemplazar(e.target.checked)} /> Reemplazar
            </label>
            <button onClick={() => fileRef.current?.click()} disabled={!cuentaId}
              data-testid="cart-import-button"
              className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-50 text-white text-sm font-medium px-3 py-1.5 rounded-sm">
              <Upload className="h-4 w-4" /> Importar cartola
            </button>
          </div>
        }
      />

      {cuentas.length === 0 && (
        <div className="bg-[#FFF8E7] border border-[#D4A373] rounded-md p-4 text-sm text-[#7A5028]">
          No tienes cuentas bancarias. <a href="/app/cuentas-bancarias" className="font-semibold underline">Crea una primero</a>.
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <KPI label="Movs" value={data.totales.count} />
          <KPI label="Cargos" value={fmtCLP(data.totales.cargos)} tone="danger" />
          <KPI label="Abonos" value={fmtCLP(data.totales.abonos)} tone="primary" />
          <KPI label="Conciliados" value={`${data.totales.conciliados} / ${data.totales.count}`} />
          <KPI label="Saldo final" value={fmtCLP(data.totales.saldo_final)} tone="primary" />
        </div>
      )}

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto">
        {loading ? <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
          : !data || data.movimientos.length === 0 ? <div className="p-8 text-center text-[#59645E] text-sm">Sin movimientos. Importa una cartola para empezar.</div>
          : (
          <table className="acc-table" data-testid="cart-table">
            <thead><tr>
              <th style={{ width: 90 }}>Fecha</th><th>Descripción</th>
              <th className="num">Cargo</th><th className="num">Abono</th><th className="num">Saldo</th>
              <th style={{ width: 90 }}>N° doc</th><th style={{ width: 70 }}>Estado</th><th style={{ width: 40 }}></th>
            </tr></thead>
            <tbody>
              {data.movimientos.map((m) => (
                <tr key={m.id} data-testid={`cart-row-${m.id}`}>
                  <td>{fmtDate(m.fecha)}</td>
                  <td className="text-[12px]">{m.descripcion || "—"}</td>
                  <td className="num text-[#A94438]">{m.cargo ? fmtCLP(m.cargo) : ""}</td>
                  <td className="num text-[#1B4332]">{m.abono ? fmtCLP(m.abono) : ""}</td>
                  <td className="num">{fmtCLP(m.saldo)}</td>
                  <td className="font-mono text-[11px]">{m.n_doc || "—"}</td>
                  <td>
                    {m.conciliado
                      ? <span className="text-[10px] bg-[#2D6A4F]/15 text-[#1B4332] px-1.5 py-0.5 rounded-sm font-medium">✓ conciliado</span>
                      : <span className="text-[10px] bg-[#F0EDE6] text-[#59645E] px-1.5 py-0.5 rounded-sm">pendiente</span>}
                  </td>
                  <td>
                    {!m.conciliado && (
                      <button onClick={() => delMov(m.id)} className="text-[#A94438] hover:bg-[#A94438]/10 p-1 rounded-sm">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Import mapping dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => !importing && setImportOpen(v)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mapear columnas de la cartola</DialogTitle>
            <DialogDescription>
              Indica qué columna del archivo corresponde a cada campo. Se detectó automáticamente la mejor coincidencia.
            </DialogDescription>
          </DialogHeader>
          {parsed && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="label-caps block mb-1">{f.label}</label>
                    <Select value={mapping[f.key] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="— No mapear —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— No mapear —</SelectItem>
                        {parsed.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="bg-[#F0EDE6] rounded-sm p-3 text-xs mb-3">
                <strong>{parsed.total_rows || parsed.rows.length}</strong> filas detectadas. Vista previa:
              </div>
              <div className="border border-[#E2DFD8] rounded-sm overflow-x-auto mb-3 max-h-60">
                <table className="acc-table">
                  <thead><tr>{parsed.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <tr key={i}>{parsed.headers.map((h) => <td key={h} className="text-[11px]">{String(r[h] ?? "")}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <button type="button" onClick={() => setImportOpen(false)}
                  className="border border-[#E2DFD8] hover:bg-[#F9F8F6] text-sm px-4 py-2 rounded-sm">Cancelar</button>
                <button type="button" onClick={submitImport} disabled={importing}
                  data-testid="submit-cart-import"
                  className="bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-sm">
                  {importing ? "Importando…" : "Importar"}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = { default: "text-[#1C211F]", primary: "text-[#1B4332]", danger: "text-[#A94438]" };
  return (
    <div className="bg-white border border-[#E2DFD8] rounded-md p-3 sm:p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
